import React, { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ProcessingProvider, useProcessing } from "./context/ProcessingContext";
import { OfflineProvider } from "./context/OfflineContext";
import { registerProcessingCallbacks } from "./utils/api";
import { Toaster } from "sonner";
import InstallPWA from "./components/InstallPWA";
import PushNotificationManager from "./components/PushNotificationManager";
import AppVersionBadge from "./components/AppVersionBadge";

// Lazy load pages
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const TermsAndConditionsPage = lazy(() => import("./pages/TermsAndConditionsPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const CRMLoginPage = lazy(() => import("./pages/CRMLoginPage"));
const TelecallerLoginPage = lazy(() => import("./pages/TelecallerLoginPage"));
const BillingLoginPage = lazy(() => import("./pages/BillingLoginPage"));
const Register = lazy(() => import("./pages/Register"));
const ShopifyConnect = lazy(() => import("./pages/ShopifyConnect"));
const PlatformSelection = lazy(() => import("./pages/PlatformSelection"));
const MerchantDashboard = lazy(() => import("./pages/merchant/Dashboard"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const B2BModule = lazy(() => import("./pages/merchant/b2b/index"));

// Admin Pages
const WeightReconciliationPage = lazy(() => import("./pages/admin/WeightReconciliationPage"));

// Staff Dashboards
const CRMDashboard = lazy(() => import("./pages/crm/Dashboard"));
const TelecallerDashboard = lazy(() => import("./pages/telecaller/Dashboard"));
const BillingDashboard = lazy(() => import("./pages/billing/Dashboard"));

// Interactive Quote Page (Public)
const InteractiveQuote = lazy(() => import("./pages/InteractiveQuote"));

// BlueDart Rates Page (Admin)
const BlueDartRatesPage = lazy(() => import("./pages/admin/BlueDartRatesPage"));
// Xpressbees Rates Page (Admin)
const XpressbeesRatesPage = lazy(() => import("./pages/admin/XpressbeesRatesPage"));

// BlueDart Rates Page (Merchant)
const MerchantBlueDartRatesPage = lazy(() => import("./pages/merchant/BlueDartRatesPage"));

// API Processing Connector - connects API calls to processing state
const ApiProcessingConnector = ({ children }) => {
  const { startProcessing, stopProcessing } = useProcessing();
  
  useEffect(() => {
    registerProcessingCallbacks(startProcessing, stopProcessing);
  }, [startProcessing, stopProcessing]);
  
  return children;
};

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, allowedType }) => {
  const { user, userType, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedType && userType !== allowedType) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, userType, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (user) {
    // For merchants with BOTH access, redirect to platform selection
    if (userType === 'merchant' && (user.merchant_type === 'BOTH' || user.b2b_enabled)) {
      return <Navigate to="/select-platform" replace />;
    }
    // For B2B only merchants, go to B2B dashboard
    if (userType === 'merchant' && user.merchant_type === 'B2B') {
      return <Navigate to="/merchant/b2b/dashboard" replace />;
    }
    // For admin or B2C only merchants
    const redirectPath = userType === 'admin' ? '/admin/dashboard' : '/merchant/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Secret Admin Login Route - Protected by Access Code */}
        <Route path="/sb-mgmt-x7k9" element={<AdminLoginPage />} />
        
        {/* Secret CRM Portal */}
        <Route path="/crm-portal" element={<CRMLoginPage />} />
        
        {/* Secret Telecaller Portal */}
        <Route path="/tele-portal" element={<TelecallerLoginPage />} />
        
        {/* Secret Billing Portal */}
        <Route path="/billing-portal" element={<BillingLoginPage />} />
        
        {/* Staff Dashboard Routes */}
        <Route path="/crm/dashboard" element={<CRMDashboard />} />
        <Route path="/telecaller/dashboard" element={<TelecallerDashboard />} />
        <Route path="/billing/dashboard" element={<BillingDashboard />} />
        
        {/* Public Interactive Quote Page */}
        <Route path="/quote/:quoteId" element={<InteractiveQuote />} />
        
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        
        {/* Password Reset Routes (Public) */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Shopify App-Store install completion bridge (public — works
            logged-in or logged-out; it self-routes through login) */}
        <Route path="/shopify/connect" element={<ShopifyConnect />} />

        {/* Public Terms of Service */}
        <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
        <Route path="/terms" element={<TermsPage />} />

        {/* Platform Selection Route */}
        <Route
          path="/select-platform"
          element={
            <ProtectedRoute allowedType="merchant">
              <PlatformSelection />
            </ProtectedRoute>
          }
        />

        {/* Merchant Routes */}
        <Route
          path="/merchant/*"
          element={
            <ProtectedRoute allowedType="merchant">
              <MerchantDashboard />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute allowedType="admin">
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* B2B Routes */}
        <Route
          path="/merchant/b2b/*"
          element={
            <ProtectedRoute allowedType="merchant">
              <B2BModule />
            </ProtectedRoute>
          }
        />

        {/* Default Route */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ProcessingProvider>
      <OfflineProvider>
        <AuthProvider>
          <BrowserRouter>
            <ApiProcessingConnector>
              <AppRoutes />
            </ApiProcessingConnector>
            <Toaster position="top-right" richColors duration={2000} closeButton />
            <InstallPWA />
            <PushNotificationManager />
            <AppVersionBadge />
          </BrowserRouter>
        </AuthProvider>
      </OfflineProvider>
    </ProcessingProvider>
  );
}

export default App;
